import PromineoTabs from "components/common/controls/PromineoTabs";
import PanelHeader from "./PanelHeader";
import RequestJsonViewer from "./RequestJsonViewer";
import Select from "components/common/controls/Select";
import OutputSpecification from "interfaces/howItWorks/OutputSpecification";
import { useEffect, useState } from "react";

interface Props {
  specification: OutputSpecification;
  groupOptions: any[];
  onSpecificationChange: (updatedSpecification: OutputSpecification) => void;
}

export default function OutputSpecificationSelection(props: Props) {
  const { onSpecificationChange } = props;
  const [outputSpecification, setOutputSpecification] =
    useState<OutputSpecification>(props.specification);

  useEffect(() => {
    setOutputSpecification(props.specification);
  }, [props.specification]);

  useEffect(() => {
    onSpecificationChange?.(outputSpecification);
  }, [outputSpecification, onSpecificationChange]);

  const Header = () => {
    return <PanelHeader stepCount={3} title="Specify output requirements" />;
  };

  const periodicGranularityOptions = [
    {
      display: "Daily",
      value: "Day",
    },
    {
      display: "Weekly",
      value: "Week",
    },
    {
      display: "Monthly",
      value: "Month",
    },
    {
      display: "Quarterly",
      value: "Quarter",
    },
    {
      display: "Yearly",
      value: "Year",
    },
  ];

  const valueTypeOptions = [
    {
      display: "Periodic",
      value: "PerQty",
    },
  ];

  const weekDaysOptions = [
    {
      display: "Monday",
      value: "Monday",
    },
    {
      display: "Tuesday",
      value: "Tuesday",
    },
    {
      display: "Wednesday",
      value: "Wednesday",
    },
    {
      display: "Thursday",
      value: "Thursday",
    },
    {
      display: "Friday",
      value: "Friday",
    },
    {
      display: "Saturday",
      value: "Saturday",
    },
    {
      display: "Sunday",
      value: "Sunday",
    },
  ];

  const OutputSpecificationOptions = () => {
    const Option = ({
      label,
      dataSource,
      value,
      onValueChange,
    }: {
      label: string;
      dataSource: any[];
      value: string;
      onValueChange: (value: string) => void;
    }) => {
      return (
        <div>
          <div className="font-medium mb-1">{label}</div>
          <Select
            className="how-it-works-select"
            value={value}
            dataSource={dataSource}
            valueExpr="value"
            displayExpr="display"
            onValueChange={(value: any) => {
              onValueChange(value);
            }}
          />
        </div>
      );
    };

    return (
      <div className="grid grid-cols-4 gap-4 px-6 pb-2">
        <Option
          label="Periodic granularity"
          dataSource={periodicGranularityOptions}
          value={outputSpecification.PointsInTime.Type}
          onValueChange={(value) => {
            setOutputSpecification({
              ...outputSpecification,
              PointsInTime: {
                ...outputSpecification.PointsInTime,
                Type: value,
              },
            });
          }}
        />
        <Option
          label="Value type"
          dataSource={valueTypeOptions}
          value={outputSpecification.Values[0].Type}
          onValueChange={(value) => {
            setOutputSpecification({
              ...outputSpecification,
              Values: [
                {
                  ...outputSpecification.Values[0],
                  Type: value,
                },
              ],
            });
          }}
        />
        <Option
          label="Selected groups"
          dataSource={props.groupOptions}
          value={outputSpecification.GroupBy[0].Field}
          onValueChange={(value) => {
            setOutputSpecification({
              ...outputSpecification,
              GroupBy: [
                {
                  ...outputSpecification.GroupBy[0],
                  Field: value,
                },
              ],
            });
          }}
        />
        <Option
          label="First day of the week"
          dataSource={weekDaysOptions}
          value={outputSpecification.PointsInTime.FirstDayOfWeek}
          onValueChange={(value) => {
            setOutputSpecification({
              ...outputSpecification,
              PointsInTime: {
                ...outputSpecification.PointsInTime,
                FirstDayOfWeek: value,
              },
            });
          }}
        />
      </div>
    );
  };

  const JsonView = () => {
    return <RequestJsonViewer json={outputSpecification} />;
  };

  return (
    <>
      <PromineoTabs
        tabs={[
          {
            component: OutputSpecificationOptions,
            title: "Options",
          },
          {
            component: JsonView,
            title: "JSON",
          },
        ]}
        tabsLabel="Viewing mode"
        header={Header}
      />
    </>
  );
}
